import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_DOMAIN, getHeaders } from '../Config/apiConfig';
import * as listCache from '../../../services/listCache';

const MIN_CHARS_DEFAULT = 2;
const DEBOUNCE_MS = 350;
const PAGE_SIZE_DEFAULT = 20;

function extractResults(raw) {
  if (!raw) return { results: [], next: null };
  if (Array.isArray(raw)) return { results: raw, next: null };
  if (Array.isArray(raw.results)) return { results: raw.results, next: raw.next || null };
  if (raw.data) {
    if (Array.isArray(raw.data)) return { results: raw.data, next: null };
    if (Array.isArray(raw.data.results)) return { results: raw.data.results, next: raw.data.next || null };
  }
  return { results: [], next: null };
}

/**
 * Backend-searched, debounced, cached multi-select for large datasets
 * (e.g. Meeting Attendees, Task Watchers) — the multi-value sibling of
 * AsyncSelect. Selections render as removable chips; the same debounced
 * page-1-cache-then-search pipeline resolves options and pre-existing values.
 *
 * Controlled: `value` is an array of selected ids; `onChange(ids)` reports
 * the full updated array.
 *
 * Required: label, value, onChange, endpoint (relative API_ROUTES path),
 * getOptionLabel(record).
 * Optional: getOptionValue (default record.id), searchParam ('search'),
 * minChars (2), pageSize (20), placeholder, required, disabled.
 */
const AsyncMultiSelect = ({
  label,
  value,
  onChange,
  endpoint,
  getOptionLabel,
  getOptionValue = (o) => o.id,
  searchParam = 'search',
  minChars = MIN_CHARS_DEFAULT,
  pageSize = PAGE_SIZE_DEFAULT,
  placeholder,
  required,
  disabled,
}) => {
  const selectedIds = Array.isArray(value) ? value : [];

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [labels, setLabels] = useState({}); // id -> resolved display label
  const [options, setOptions] = useState([]);
  const [page1Options, setPage1Options] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nextUrl, setNextUrl] = useState(null);
  const [highlighted, setHighlighted] = useState(-1);

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const listboxId = useRef(`asyncmultiselect-listbox-${Math.random().toString(36).slice(2)}`).current;

  // Resolve a display label for every selected id we don't have one for yet —
  // covers ids loaded from a record on mount, before any search has run.
  useEffect(() => {
    const unresolved = selectedIds.filter((id) => labels[id] === undefined);
    if (!unresolved.length) return;
    let cancelled = false;
    Promise.all(
      unresolved.map((id) =>
        listCache.fetchDeduped(`${API_DOMAIN}${endpoint}${id}/`, getHeaders())
          .then((raw) => {
            const record = raw?.data ?? raw;
            return [id, record ? getOptionLabel(record) : `#${id}`];
          })
          .catch(() => [id, `#${id}`])
      )
    ).then((pairs) => {
      if (cancelled) return;
      setLabels((prev) => {
        const next = { ...prev };
        pairs.forEach(([id, lbl]) => { next[id] = lbl; });
        return next;
      });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds.join(','), endpoint]);

  // Preload + cache page 1 on mount — feeds instant local prefix search and
  // gives the dropdown something to show before the user types anything.
  useEffect(() => {
    const url = `${API_DOMAIN}${endpoint}?page_size=${pageSize}`;
    const cached = listCache.get(url);
    if (cached) {
      setPage1Options(extractResults(cached.data).results);
      if (!cached.isStale) return;
    }
    listCache.fetchDeduped(url, getHeaders())
      .then((raw) => {
        listCache.set(url, raw);
        setPage1Options(extractResults(raw).results);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, pageSize]);

  const searchLocal = useCallback((term) => {
    const tokens = term.toLowerCase().split(/\s+/).filter(Boolean);
    if (!tokens.length) return [];
    return page1Options.filter((opt) => {
      const lbl = (getOptionLabel(opt) || '').toLowerCase();
      const words = lbl.split(/\s+/);
      return tokens.every((tok) => words.some((w) => w.startsWith(tok)));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page1Options]);

  const runSearch = useCallback((term, url) => {
    setLoading(true);
    listCache.fetchDeduped(url, getHeaders())
      .then((raw) => {
        const { results, next } = extractResults(raw);
        listCache.set(url, raw);
        setOptions(results);
        setNextUrl(next);
        setHighlighted(results.length ? 0 : -1);
      })
      .catch(() => { setOptions([]); setNextUrl(null); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const term = query.trim();
    if (term.length < minChars) {
      setOptions(term.length === 0 ? page1Options : []);
      setNextUrl(null);
      setLoading(false);
      return;
    }

    const local = searchLocal(term);
    if (local.length > 0) {
      setOptions(local);
      setNextUrl(null);
      setHighlighted(0);
      setLoading(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      const url = `${API_DOMAIN}${endpoint}?${searchParam}=${encodeURIComponent(term)}&page_size=${pageSize}`;
      const cached = listCache.get(url);
      if (cached && !cached.isStale) {
        const { results, next } = extractResults(cached.data);
        setOptions(results);
        setNextUrl(next);
        setHighlighted(results.length ? 0 : -1);
      } else {
        runSearch(term, url);
      }
    }, DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, open, endpoint, searchParam, pageSize, minChars, runSearch, searchLocal, page1Options]);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  const isSelected = (id) => selectedIds.includes(id);

  const addValue = (opt) => {
    const id = getOptionValue(opt);
    const lbl = getOptionLabel(opt);
    setLabels((prev) => ({ ...prev, [id]: lbl }));
    if (!isSelected(id)) onChange([...selectedIds, id]);
    setQuery('');
    setHighlighted(-1);
    inputRef.current?.focus();
  };

  const removeValue = (id, e) => {
    e?.stopPropagation();
    onChange(selectedIds.filter((v) => v !== id));
  };

  const loadMore = () => {
    if (!nextUrl) return;
    setLoading(true);
    listCache.fetchDeduped(nextUrl, getHeaders())
      .then((raw) => {
        const { results, next } = extractResults(raw);
        setOptions((prev) => [...prev, ...results]);
        setNextUrl(next);
      })
      .finally(() => setLoading(false));
  };

  const visibleOptions = options.filter((o) => !isSelected(getOptionValue(o)));

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setHighlighted((i) => Math.min(i + 1, visibleOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && highlighted >= 0 && visibleOptions[highlighted]) {
        e.preventDefault();
        addValue(visibleOptions[highlighted]);
      }
    } else if (e.key === 'Backspace' && !query && selectedIds.length) {
      removeValue(selectedIds[selectedIds.length - 1]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const term = query.trim();
  const belowMinChars = term.length > 0 && term.length < minChars;
  const showNoResults = open && !loading && term.length >= minChars && visibleOptions.length === 0;
  const activeOptionId = highlighted >= 0 && visibleOptions[highlighted]
    ? `${listboxId}-opt-${getOptionValue(visibleOptions[highlighted])}`
    : undefined;

  return (
    <div className="form-multiselect-wrap form-asyncselect-wrap" ref={containerRef}>
      <label className="form-label">
        {label}
        {required && <span className="req">*</span>}
        {selectedIds.length > 0 && <span className="form-multiselect-count">{selectedIds.length}</span>}
      </label>

      {selectedIds.length > 0 && (
        <div className="form-multiselect-chips">
          {selectedIds.map((id) => (
            <span key={id} className="form-multiselect-chip">
              {labels[id] ?? `#${id}`}
              {!disabled && (
                <button
                  type="button"
                  className="form-multiselect-chip-x"
                  onClick={(e) => removeValue(id, e)}
                  aria-label={`Remove ${labels[id] ?? id}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      <div className={`form-asyncselect-input-wrap${open ? ' is-open' : ''}`}>
        <input
          ref={inputRef}
          type="text"
          className="form-input form-asyncselect-input"
          value={query}
          disabled={disabled}
          placeholder={placeholder || `Type to search ${label.toLowerCase()}…`}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-activedescendant={activeOptionId}
        />
        {loading && <span className="form-asyncselect-spinner spinner" aria-hidden="true" />}
      </div>

      {open && (
        <div className="form-multiselect-dropdown" role="listbox" id={listboxId}>
          {belowMinChars && (
            <div className="form-multiselect-empty">Type at least {minChars} characters…</div>
          )}
          {!belowMinChars && term.length === 0 && visibleOptions.length === 0 && (
            <div className="form-multiselect-empty">Start typing to search…</div>
          )}
          {showNoResults && (
            <div className="form-multiselect-empty">No results for "{term}"</div>
          )}
          {visibleOptions.map((opt, i) => {
            const id = getOptionValue(opt);
            return (
              <div
                key={id}
                id={`${listboxId}-opt-${id}`}
                role="option"
                aria-selected={false}
                className={`form-multiselect-option${i === highlighted ? ' is-selected' : ''}`}
                onMouseEnter={() => setHighlighted(i)}
                onClick={() => addValue(opt)}
              >
                {getOptionLabel(opt)}
              </div>
            );
          })}
          {nextUrl && !loading && (
            <div
              className="form-multiselect-option form-asyncselect-loadmore"
              onClick={loadMore}
            >
              Load more results…
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AsyncMultiSelect;
